import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, KeyRound, Lock, Plus, ShieldCheck, UserRoundX } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { invalidate } from "@/hooks/use-resource";
import { Skeleton, UserAvatar } from "@/components/common";
import { BarAction, Chips, PageBar, QuietSearch } from "@/components/layout/PageBar";
import { brukereApi, lastSeen, userState } from "@/lib/brukere";
import { StateText } from "@/components/brukere/Fields";
import { UserSheet } from "@/components/brukere/UserSheet";
import { NewUserSheet } from "@/components/brukere/NewUserSheet";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function UserRow({ u, me, onOpen }) {
  const state = userState(u);
  const admin = u.flags?.user_admin;
  const extra = [u.flags?.settings_admin ? "Satser" : null, u.flags?.workspace_admin ? "Tømme" : null].filter(Boolean);
  return (
    <button type="button" onClick={() => onOpen(u)} className={cn("group flex w-full items-center gap-4 rounded-[14px] px-3 py-3 text-left transition-colors duration-150 hover:bg-[hsl(210_20%_97.5%)] focus-visible:bg-[hsl(210_20%_97.5%)] focus-visible:outline-none", !u.is_active && "opacity-60")} data-testid={`user-row-${u.id}`} data-state={state?.key || "ok"}>
      <UserAvatar name={u.name} initials={u.initials} color={u.color} src={u.avatar_url} size="lg" className={cn("h-10 w-10 text-[13px]", !u.is_active && "grayscale")} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-[15px] font-medium tracking-[-0.01em]" data-testid={`user-row-name-${u.id}`}>{u.name}</span>
          {u.id === me ? <span className="shrink-0 text-[12.5px] text-muted-foreground">deg</span> : null}
        </div>
        <div className="truncate text-[13px] text-muted-foreground">{[u.email, u.title, u.dept_label].filter(Boolean).join(" · ")}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 md:hidden">
          <span className="text-[12.5px] text-foreground/75">{u.role_label || u.role || "Ingen rolle"}</span>
          {state ? <StateText state={state} /> : null}
        </div>
      </div>
      <div className="hidden w-[170px] shrink-0 md:block">
        <div className="text-[13.5px] text-foreground/85" data-testid={`user-row-role-${u.id}`}>{u.role_label || <span className="text-[hsl(var(--v-assumed))]">{u.role || "Ingen rolle"}</span>}</div>
        {admin || extra.length ? <div className="mt-0.5 flex items-center gap-1 text-[12px] text-muted-foreground" title={[admin ? "Brukeradministrator" : null, ...extra].filter(Boolean).join(" · ")}><ShieldCheck className="h-3 w-3" />{[admin ? "Administrator" : null, ...extra].filter(Boolean).join(" · ")}</div> : null}
      </div>
      <div className="hidden w-[210px] shrink-0 text-right md:block">
        {state ? <StateText state={state} className="justify-end" testId={`user-row-state-${u.id}`} /> : null}
        <div className="text-[12.5px] text-muted-foreground" data-testid={`user-row-seen-${u.id}`}>{lastSeen(u)}</div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
    </button>
  );
}

function MemberRow({ m, onGrant }) {
  return (
    <div className="group flex items-center gap-4 rounded-[14px] px-3 py-2.5" data-testid={`member-row-${m.id}`}>
      <UserAvatar name={m.name} initials={m.initials} color={m.color} size="lg" className="h-9 w-9 text-[12px] opacity-80" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-medium">{m.name}</div>
        <div className="truncate text-[12.5px] text-muted-foreground">{[m.title, m.dept_label].filter(Boolean).join(" · ") || "Teammedlem"}</div>
      </div>
      <button type="button" onClick={() => onGrant(m)} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-black/[0.1] bg-white px-3 text-[12.5px] font-medium text-foreground/80 transition-[opacity,border-color] hover:border-black/[0.25] hover:text-foreground md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" data-testid={`member-grant-${m.id}`}><KeyRound className="h-3.5 w-3.5" /> Gi innlogging</button>
    </div>
  );
}

function Locked({ status }) {
  const admins = status?.admins || [];
  return (
    <div className="mx-auto mt-16 max-w-[460px] text-center" data-testid="users-locked">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-black/[0.04]"><Lock className="h-5 w-5 text-muted-foreground" /></div>
      <h2 className="mt-4 text-[20px] font-semibold tracking-[-0.015em]">Brukere styres av en administrator</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">Du kan se teamet og hvem som kan hva under Innstillinger. Nye brukere, roller og passord ordnes av {admins.length ? admins.map((a) => a.name).join(admins.length > 2 ? ", " : " eller ") : "en brukeradministrator"}.</p>
      {admins.length ? <div className="mt-5 flex justify-center -space-x-2">{admins.map((a) => <UserAvatar key={a.id} name={a.name} initials={a.initials} color={a.color} src={a.avatar_url} size="lg" className="h-10 w-10 text-[13px] ring-2 ring-white" />)}</div> : null}
      <Link to="/settings#settings-access" className="mt-6 inline-flex h-9 items-center rounded-full border border-black/[0.1] bg-white px-4 text-[13px] font-medium hover:border-black/[0.25]" data-testid="users-locked-access-link">Se tilgang og roller</Link>
    </div>
  );
}

/** Innstillinger → Brukere. One list of people with a login; one sheet per person; one primary action: «Ny bruker». */
export default function UsersPage() {
  const { user: me, applySession } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("alle");
  const searchRef = useRef(null);
  const canAdmin = me?.user_admin === true;

  const load = useCallback(() => {
    if (!canAdmin) { brukereApi.status().then((r) => setStatus(r.data)).catch((e) => setError(errMsg(e))); return; }
    brukereApi.list().then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [canAdmin]);
  useEffect(() => { load(); }, [load]);

  const openId = params.get("bruker");
  const nyParam = params.get("ny");
  const setParam = (k, v) => { const n = new URLSearchParams(params); ["bruker", "ny"].forEach((x) => n.delete(x)); if (v) n.set(k, v); setParams(n, { replace: true }); };
  const open = (u) => setParam("bruker", u.id);
  const close = () => setParam("bruker", null);

  const users = useMemo(() => data?.users || [], [data]);
  const members = useMemo(() => data?.members || [], [data]);
  const selected = users.find((u) => u.id === openId) || null;
  const member = nyParam && nyParam !== "1" ? members.find((m) => m.id === nyParam) || null : null;
  const newOpen = Boolean(data) && (nyParam === "1" || Boolean(member));

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (e.metaKey || e.ctrlKey || e.altKey || (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable))) return;
      if (document.querySelector("[role=dialog]")) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); setParam("ny", "1"); }
      if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); }
    };
    if (!canAdmin) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const counts = useMemo(() => ({
    alle: users.length,
    aktive: users.filter((u) => u.is_active).length,
    venter: users.filter((u) => u.is_active && u.must_change_password).length,
    deaktivert: users.filter((u) => !u.is_active).length,
  }), [users]);
  const list = useMemo(() => {
    const nq = norm(q.trim());
    return users
      .filter((u) => filter === "alle" || (filter === "aktive" && u.is_active) || (filter === "venter" && u.is_active && u.must_change_password) || (filter === "deaktivert" && !u.is_active))
      .filter((u) => !nq || norm([u.name, u.email, u.title, u.role_label, u.dept_label].join(" ")).includes(nq))
      .sort((a, b) => (a.is_active === b.is_active ? (a.name || "").localeCompare(b.name || "", "nb") : a.is_active ? -1 : 1));
  }, [users, q, filter]);
  const memberList = useMemo(() => { const nq = norm(q.trim()); return members.filter((m) => !nq || norm([m.name, m.title, m.dept_label].join(" ")).includes(nq)); }, [members, q]);

  const changed = (u) => {
    setData((d) => (d ? { ...d, users: d.users.some((x) => x.id === u.id) ? d.users.map((x) => (x.id === u.id ? u : x)) : [...d.users, u] } : d));
    invalidate("/team"); invalidate("/brukere");
    if (u.id === me?.id) applySession({ ...me, name: u.name, title: u.title, role: u.role, department: u.department, email: u.email, avatar_url: u.avatar_url, ...u.flags });
  };
  const created = (u) => { changed(u); if (member) setData((d) => (d ? { ...d, members: d.members.filter((m) => m.id !== member.id) } : d)); };

  const meta = data ? [`${counts.aktive} aktive`, counts.venter ? `${counts.venter} venter på innlogging` : null, counts.deaktivert ? `${counts.deaktivert} deaktivert` : null].filter(Boolean).join(" · ") : null;
  const chipItems = [["alle", "Alle", counts.alle], ["aktive", "Aktive", counts.aktive], counts.venter ? ["venter", "Venter på innlogging", counts.venter] : null, counts.deaktivert ? ["deaktivert", "Deaktivert", counts.deaktivert] : null].filter(Boolean);

  return (
    <div className="mx-auto w-full max-w-[1000px] px-5 pb-20 pt-5 sm:px-8" data-testid="users-page">
      <PageBar title="Brukere" meta={meta} back={{ to: "/settings", label: "Til innstillinger" }} testId="users-bar"
        action={canAdmin ? <BarAction primary onClick={() => setParam("ny", "1")} testId="users-new-button"><Plus className="h-4 w-4" /> Ny bruker</BarAction> : null} />

      {!canAdmin ? (status ? <Locked status={status} /> : error ? <p className="mt-6 text-[13px] text-[hsl(var(--v-blocked))]">{error}</p> : <Skeleton className="mt-6 h-40" />) : !data ? (
        error ? <p className="mt-6 text-[13px] text-[hsl(var(--v-blocked))]" data-testid="users-error">{error}</p> : <div className="mt-6 space-y-2"><Skeleton className="h-14" /><Skeleton className="h-14" /><Skeleton className="h-14" /></div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <QuietSearch value={q} onChange={setQ} inputRef={searchRef} placeholder="Søk etter navn, e-post eller rolle" className="sm:max-w-[360px] sm:flex-1" testId="users-search" />
            <Chips value={filter} onChange={setFilter} items={chipItems} label="Filter" testId="users-filter" />
          </div>

          <div className="mt-3 hidden items-center gap-4 px-3 pb-1.5 text-[12px] font-medium text-muted-foreground md:flex">
            <span className="w-10" /><span className="flex-1">Person</span><span className="w-[170px]">Rolle</span><span className="w-[210px] text-right">Status</span><span className="w-4" />
          </div>
          <div className="-mx-3 space-y-0.5 sm:mx-0 md:-mx-0" data-testid="users-list">
            {list.length ? list.map((u) => <UserRow key={u.id} u={u} me={me?.id} onOpen={open} />) : (
              <div className="px-3 py-10 text-center text-[14px] text-muted-foreground" data-testid="users-empty">{q ? <>Ingen treff på «{q}». <button type="button" onClick={() => { setQ(""); setFilter("alle"); }} className="font-medium text-foreground underline-offset-2 hover:underline">Vis alle</button></> : "Ingen brukere i dette utvalget."}</div>
            )}
          </div>

          {memberList.length ? (
            <section className="mt-10" data-testid="users-members">
              <div className="flex items-baseline justify-between gap-3 px-3">
                <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-[-0.01em]"><UserRoundX className="h-4 w-4 text-muted-foreground" /> Uten innlogging <span className="font-mono text-[12px] font-normal text-muted-foreground">{memberList.length}</span></h2>
                <Link to="/settings#settings-team" className="text-[12.5px] text-muted-foreground hover:text-foreground">Teamlisten</Link>
              </div>
              <p className="mt-1 px-3 text-[13px] text-muted-foreground">Kan tildeles poster og nevnes i notater. Gi innlogging når de skal jobbe i systemet selv – tildelt arbeid følger med.</p>
              <div className="mt-2 space-y-0.5">{memberList.map((m) => <MemberRow key={m.id} m={m} onGrant={(x) => setParam("ny", x.id)} />)}</div>
            </section>
          ) : null}

          <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 px-3 text-[12.5px] text-muted-foreground" data-testid="users-footer">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Passord lagres bare som hash. Midlertidige passord vises én gang.</span>
            <Link to="/settings#settings-access" className="hover:text-foreground" data-testid="users-access-link">Se hvem som kan hva →</Link>
            <span className="hidden items-center gap-1.5 md:inline-flex"><kbd className="rounded border border-black/[0.12] bg-white px-1.5 font-mono text-[10.5px]">N</kbd> ny bruker <kbd className="ml-2 rounded border border-black/[0.12] bg-white px-1.5 font-mono text-[10.5px]">/</kbd> søk</span>
          </div>
        </>
      )}

      {canAdmin ? <UserSheet user={selected} data={data} me={me?.id} onClose={close} onChanged={changed} /> : null}
      {canAdmin ? <NewUserSheet open={newOpen} member={member} data={data} onClose={() => setParam("ny", null)} onCreated={created} /> : null}
      {canAdmin && nyParam && nyParam !== "1" && data && !member ? <MissingMember onDone={() => { toast.error("Fant ikke teammedlemmet – det kan allerede ha fått innlogging"); setParam("ny", null); }} /> : null}
      {canAdmin && openId && data && !selected ? <MissingMember onDone={() => { toast.error("Fant ikke brukeren"); close(); }} /> : null}
    </div>
  );
}

function MissingMember({ onDone }) {
  useEffect(() => { onDone(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
